import { useState, useEffect, useCallback } from "react";
import {
  SimpleGrid,
  Title,
  Paper,
  Text,
  Group,
  RingProgress,
  Stack,
  Badge,
  Loader,
  Center,
  ThemeIcon,
  Progress,
  Box,
} from "@mantine/core";
import {
  IconPhoto,
  IconCheck,
  IconX,
  IconClock,
  IconChartBar,
} from "@tabler/icons-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

interface Stats {
  totalImages: number;
  editedImages: number;
  deniedImages: number;
  newImages: number;
  progress: number;
}

interface DetectStat {
  _id: string;
  count: number;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Өглөөний мэнд";
  if (hour < 18) return "Өдрийн мэнд";
  return "Оройн мэнд";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [detects, setDetects] = useState<DetectStat[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [statsRes, detectRes] = await Promise.all([
        api.dashboard.stats(),
        api.dashboard.detect(),
      ]);
      setStats(statsRes.data);
      setDetects(detectRes.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Center h={400}>
        <Loader />
      </Center>
    );
  }

  const totalDetects = detects.reduce((sum, d) => sum + d.count, 0);

  return (
    <Stack gap="lg">
      {/* Greeting */}
      <div>
        <Title order={3}>
          {getGreeting()}, {user?.nickname}
        </Title>
        <Text size="sm" c="dimmed">
          GTS Dataset — Өнөөдрийн тойм
        </Text>
      </div>

      {stats && (
        <>
          {/* Stats Cards */}
          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }}>
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Нийт зураг
                  </Text>
                  <Text size="xl" fw={700} mt={4}>
                    {stats.totalImages}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    {stats.newImages} шинэ
                  </Text>
                </div>
                <ThemeIcon color="blue" variant="light" size={48} radius="md">
                  <IconPhoto size={24} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Тэмдэглэсэн
                  </Text>
                  <Text size="xl" fw={700} mt={4}>
                    {stats.editedImages}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    {stats.progress}% дууссан
                  </Text>
                </div>
                <ThemeIcon color="green" variant="light" size={48} radius="md">
                  <IconCheck size={24} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Татгалзсан
                  </Text>
                  <Text size="xl" fw={700} mt={4}>
                    {stats.deniedImages}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    аудитаар цуцлагдсан
                  </Text>
                </div>
                <ThemeIcon color="red" variant="light" size={48} radius="md">
                  <IconX size={24} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Шинэ
                  </Text>
                  <Text size="xl" fw={700} mt={4}>
                    {stats.newImages}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    тэмдэглэгээ хүлээж буй
                  </Text>
                </div>
                <ThemeIcon color="yellow" variant="light" size={48} radius="md">
                  <IconClock size={24} />
                </ThemeIcon>
              </Group>
            </Paper>
          </SimpleGrid>

          {/* Middle Row */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Progress Panel */}
            <Paper withBorder p="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text fw={600} size="sm">
                  Ахиц дэвшил
                </Text>
                <Badge variant="light" size="sm">
                  {stats.progress}%
                </Badge>
              </Group>

              <Group justify="center" mb="lg">
                <RingProgress
                  size={160}
                  thickness={14}
                  roundCaps
                  sections={[
                    {
                      value:
                        stats.totalImages > 0
                          ? (stats.editedImages / stats.totalImages) * 100
                          : 0,
                      color: "green",
                    },
                    {
                      value:
                        stats.totalImages > 0
                          ? (stats.deniedImages / stats.totalImages) * 100
                          : 0,
                      color: "red",
                    },
                    {
                      value:
                        stats.totalImages > 0
                          ? (stats.newImages / stats.totalImages) * 100
                          : 0,
                      color: "gray",
                    },
                  ]}
                  label={
                    <Text ta="center" fw={700} size="lg">
                      {stats.progress}%
                    </Text>
                  }
                />
              </Group>

              <Stack gap="xs">
                <Group justify="space-between">
                  <Group gap="xs">
                    <Box
                      w={10}
                      h={10}
                      style={{
                        borderRadius: "50%",
                        backgroundColor: "var(--mantine-color-green-6)",
                      }}
                    />
                    <Text size="xs">Тэмдэглэсэн</Text>
                  </Group>
                  <Text size="xs" fw={600}>
                    {stats.editedImages}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Group gap="xs">
                    <Box
                      w={10}
                      h={10}
                      style={{
                        borderRadius: "50%",
                        backgroundColor: "var(--mantine-color-red-6)",
                      }}
                    />
                    <Text size="xs">Татгалзсан</Text>
                  </Group>
                  <Text size="xs" fw={600}>
                    {stats.deniedImages}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Group gap="xs">
                    <Box
                      w={10}
                      h={10}
                      style={{
                        borderRadius: "50%",
                        backgroundColor: "var(--mantine-color-gray-5)",
                      }}
                    />
                    <Text size="xs">Шинэ</Text>
                  </Group>
                  <Text size="xs" fw={600}>
                    {stats.newImages}
                  </Text>
                </Group>
              </Stack>
            </Paper>

            {/* Detection Stats Panel */}
            <Paper withBorder p="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text fw={600} size="sm">
                  Таних нэрсийн тархалт
                </Text>
                <Badge variant="light" size="sm">
                  {totalDetects} нийт
                </Badge>
              </Group>

              {detects.length > 0 ? (
                <Stack gap="sm">
                  {detects.slice(0, 12).map((d) => (
                    <div key={d._id}>
                      <Group justify="space-between" mb={4}>
                        <Text size="xs" fw={500}>
                          {d._id}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {d.count} (
                          {totalDetects > 0
                            ? Math.round((d.count / totalDetects) * 100)
                            : 0}
                          %)
                        </Text>
                      </Group>
                      <Progress
                        value={
                          totalDetects > 0
                            ? (d.count / totalDetects) * 100
                            : 0
                        }
                        size="sm"
                        radius="xl"
                        color="blue"
                      />
                    </div>
                  ))}
                </Stack>
              ) : (
                <Center h={200}>
                  <Stack align="center" gap="xs">
                    <ThemeIcon
                      size={48}
                      variant="light"
                      color="gray"
                      radius="xl"
                    >
                      <IconChartBar size={24} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Тэмдэглэгээ байхгүй
                    </Text>
                  </Stack>
                </Center>
              )}
            </Paper>
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}
